<template>
  <div>
    <LoadingOverlay v-if="isLoading" />
    <b-container
      v-else
      :fluid="true"
    >
      <b-row>
        <b-col cols="9">
          <h1>Managers</h1>
        </b-col>
        <b-col class="center">
          <button
            class="custom-button blue rounded uk-margin-remove"
            @click="showAddManagerModal"
          >
            Add a Manager
          </button>
        </b-col>
      </b-row>
    </b-container>
    <hr>
    <div v-if="managers.length">
      <div
        v-for="manager in managers"
        :key="manager.id"
        class="list-item d-flex align-items-stretch"
      >
        <div class="d-flex flex-row align-self-center p-2 w-100">
          <div class="d-flex mr-auto p-2 list-item-title text-truncate">
            <span class="align-self-center text-truncate">{{ manager.user.name }}&nbsp;({{ manager.user.email }})</span>
          </div>
          <div
            class="d-flex m-2 align-content-center text-center"
          >
            <b-button
              variant="danger"
              @click="showDeleteManagerModal(manager.id)"
            >
              Delete
            </b-button>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      {{ emptyMessage }}
    </div>
    <ModalOverlay
      id="addManagerModal"
      ref="addManagerModal"
      modal-id="addManagerModal"
      :modal-show="showAddManager"
      :title="`Add New Manager`"
      size="lg"
      ok-title="Add Manager"
      @confirmed="submitAddManager"
      @hide="addManagerModalClosed"
    >
      <div class="d-block">
        <!-- eslint-disable-next-line vue/valid-v-on -->
        <b-form
          v-if="showAddManager"
          @submit.prevent="submitAddManager"
        >
          <b-form-group
            id="managerEmail"
            label="Manager Email"
            description="Email must belong to a registered user"
            label-for="managerEmailInput"
            :invalid-feedback="addManagerError"
            :state="addManagerHasError"
            label-sr-only
          >
            <b-form-input
              id="managerEmailInput"
              v-model="newManager.email"
              type="text"
              required
              placeholder="Enter manager email"
              :state="addManagerHasError"
            />
          </b-form-group>
        </b-form>
      </div>
    </ModalOverlay>
    <ModalOverlay
      id="deleteManagerModal"
      ref="deleteManagerModal"
      modal-id="deleteManagerModal"
      size="md"
      title="Delete Confirmation"
      ok-title="Delete"
      ok-variant="danger"
      @hide="deleteManagerModalClosed"
      @confirmed="submitDeleteManager"
    >
      <div
        v-if="managerBeingDeleted"
        class="d-block"
        style=""
      >
        <span>
          <b-icon-alert-triangle-fill />
          You are revoking management abilities from {{ managerBeingDeleted.user.name }}&nbsp;
          ({{ managerBeingDeleted.user.email }}).
        </span>
        <div class="text-center p-t-10">
          <span class="font-weight-bold">Do you wish to continue?</span>
        </div>
      </div>
    </ModalOverlay>
  </div>
</template>

<script>
import LoadingOverlay from '../../common/LoadingOverlay';
import axios from 'axios';
import find from 'lodash/find';
import ModalOverlay from '../../common/ModalOverlay';
import { emitToast, VARIANT_TYPE } from '../../../util/Toast';
import { getStatusFromResponseIfExists, HTTP_STATUS_CODE } from '../../../util';
import { mapState } from 'vuex';

export default {
  name: 'Managers',
  components: {
    LoadingOverlay,
    ModalOverlay,
  },
  data: () => ({
    addManagerError: '',
    emptyMessage: 'Loading Managers...',
    isLoading: true,
    managerBeingDeleted: null,
    managers: [],
    newManager: {},
    showAddManager: false,
  }),
  computed: {
    ...mapState({
      organization: state => state.organization,
    }),
    addManagerHasError() {
      return this.addManagerError.length > 0 ? false:  undefined;
    },
  },
  created() {
    this.getManagers();
  },
  methods: {

    addManagerModalClosed() {
      this.addManagerError = '';
    },

    deleteManagerModalClosed() {
      this.managerBeingDeleted = null;
    },

    async getManagers() {
      const response = await axios.get(`/api/${this.organization.slug}/manage/manager`);
      this.managers = response.data;
      this.emptyMessage = 'There are no managers';
      this.isLoading = false;
    },

    showAddManagerModal(){
      this.showAddManager = true;
      this.$refs.addManagerModal.show();
    },

    showDeleteManagerModal(mangerId) {
      this.managerBeingDeleted = find(this.managers,['id',mangerId]);
      if (this.managerBeingDeleted)
        this.$refs.deleteManagerModal.show();
    },

    async submitAddManager(e) {
      e.preventDefault();
      const { email } = this.newManager;
      try {
        const { data } = await axios.post(`/api/${this.organization.slug}/manage/manager`, {
          email,
        });
        this.managers.push(data);
        emitToast(this.$bvToast, VARIANT_TYPE.SUCCESS, `${data.user.name} added to managers!`);
        this.$refs.addManagerModal.hide();
      } catch ({ response }) {
        switch (getStatusFromResponseIfExists(response)) {
          case HTTP_STATUS_CODE.NOT_FOUND:
            this.addManagerError = 'The user could not be found';
            break;
          case HTTP_STATUS_CODE.CONFLICT:
            this.addManagerError = 'This user is already a manager.';
            break;
          case HTTP_STATUS_CODE.NOT_ACCEPTABLE:
            this.addManagerError = 'You cannot add yourself as a manager.';
            break;
          default: this.addManagerError = 'An unknown error occurred. Please try again.';
        }
      }
    },

    async submitDeleteManager(){
      try {
        const managerId = this.managerBeingDeleted.id;
        await axios.post(`/api/${this.organization.slug}/manage/manager/delete`, {
          managerId,
        });
        this.managers = this.managers.filter(manager => manager.id !== managerId);
        emitToast(this.$bvToast, VARIANT_TYPE.SUCCESS, 'Manager deleted!');
      } catch (e){
        const { response } = e;
        let errorMessage;
        switch (getStatusFromResponseIfExists(response)){
          case HTTP_STATUS_CODE.FORBIDDEN:
            errorMessage = 'You cannot remove yourself from the manger role.';
            break;
          case HTTP_STATUS_CODE.NOT_FOUND:
            errorMessage = 'The user is not a manager of this organization';
            break;
          default: errorMessage = 'An unknown error occurred.';
        }
        emitToast(this.$bvToast, VARIANT_TYPE.WARNING, errorMessage);
      }
      this.$refs.deleteManagerModal.hide();
    },
  },
};
</script>

<style scoped>

</style>
